import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef, ElementRef, Renderer2 } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Offer } from '../../../model/offer';
import { ResponsiveMode } from '../../../enum/responsiveModes';
import { UiNewViewMode } from '../ui-new-tabs-bar/ui-new-tabs-bar.component';
import { SharedService, UiOfferTab } from '../../../_services/shared.service';
import { ThemeService } from '../../../_services/theme.service';
import { ShoppingCartService } from '../../../_services/shopping-cart.service';
import { LxResponsiveClassDirective } from '../../../directives/lx-responsive-class.directive';
import { LxViewportDirective } from '../../../directives/lx-viewport.directive';
import { ThemeHighlightDirective } from '../../../directives/theme-highlight.directive';
import Utils from '../../../_utils/utils';
import { Subscription } from 'rxjs';
import { UiNewOfferPhotoTermsComponent } from '../ui-new-offer-photo-terms/ui-new-offer-photo-terms.component';

@Component({
  selector: 'ui-new-offer-content',
  standalone: true,
  imports: [CommonModule, MatCardModule, LxResponsiveClassDirective, LxViewportDirective, ThemeHighlightDirective],
  templateUrl: './ui-new-offer-content.component.html',
  styleUrls: ['./ui-new-offer-content.component.scss']
})
export class UiNewOfferContentComponent implements OnInit, OnDestroy {
  @Input() offers!: Offer[] | null;
  @Input() offer!: Offer | null;
  @Input() index: number = 0;
  @Input() viewMoreToggle: boolean = false;
  @Input() propertyId: string = '';
  @Input() offerAliasId: string = '';

  @Input() viewMode: ResponsiveMode | UiNewViewMode = ResponsiveMode.horizontal;
  responsiveMode = ResponsiveMode;
  collapsed: boolean = true;
  MIN_LIST_INDEX: number = 1;
  max_list_index: number = 2;

  private breakpointSubscription?: Subscription;

  constructor(
    private responsive: BreakpointObserver,
    public sharedService: SharedService,
    public themeService: ThemeService,
    public shoppingCartService: ShoppingCartService,
    private cdr: ChangeDetectorRef,
    private hostRef: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {}

  /**
   * Template helpers consume `viewMode` in two different ways:
   * - as a `ResponsiveMode` enum (horizontal/vertical)
   * - as a `UiNewViewMode` string ('tiles'|'list') provided by parent controls
   * This getter normalizes checks for horizontal mode.
   */
  isHorizontalMode(): boolean {
    if (typeof this.viewMode === 'string') {
      return this.viewMode === 'tiles';
    }
    return this.viewMode === ResponsiveMode.horizontal;
  }

  isVerticalMode(): boolean {
    return !this.isHorizontalMode();
  }

  
// viewMode: UiNewViewMode
  ngOnInit(): void {
    this.breakpointSubscription = this.responsive.observe([Breakpoints.HandsetPortrait])
      .subscribe(result => {
        const isHandset = result.breakpoints[Breakpoints.HandsetPortrait];
        this.MIN_LIST_INDEX = isHandset ? 0 : 1;
        this.viewMode = isHandset ? ResponsiveMode.vertical : ResponsiveMode.horizontal;
        this.cdr.detectChanges();
      });

    const activeTab = this.sharedService.getOfferCurrentActiveFilter() as UiOfferTab;
    this.sharedService.markUpgradeTabSeen(activeTab);
  }

  ngOnDestroy(): void {
    this.breakpointSubscription?.unsubscribe();
  }

  goToEarlyCheckInTab(): void {
    this.sharedService.navigateToOfferTab('ECI');
  }

  goToLateCheckoutTab(): void {
    this.sharedService.navigateToOfferTab('LCO');
  }

  goToAddonTab(): void {
    this.sharedService.navigateToOfferTab('ANCILLARY');
  }

  public checkOut() {
    this.shoppingCartService.openCheckOutOfferTermsModal(
      this.shoppingCartService.offerAliasId,
      this.shoppingCartService.propId,
      UiNewOfferPhotoTermsComponent,
    );
  }

  goToNextTab(): void {

    const offersInCart = this.shoppingCartService.getNumberOfOffersInShoppingCart(this.offerAliasId);
    const next = this.sharedService.getNextUpgradeAction(offersInCart);

    if (!next) return;
    if (next.action === 'checkout') {
      this.checkOut();
      return;
    }

    this.sharedService.navigateToOfferTab(next.tab);
  }

  addToCartAndContinue(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    try {
      this.addOfferToCart();
    } finally {
      this.goToNextTab();
    }
  }


  addOfferToCart(): void {
    if (this.offer) {
      this.shoppingCartService.addOfferToCart(this.offer, this.offerAliasId, false);
    }
  }

  removeOfferFromCart(): void {
    if (this.offer) {
      this.shoppingCartService.removeOfferFromCart(this.offer, this.offerAliasId);
    }
  }

  requestUpgrade(): void {
    if (this.offer) {
      this.shoppingCartService.addOfferToCart(this.offer, this.offerAliasId, false);
      this.shoppingCartService.openCheckOutOfferTermsModal(
        this.shoppingCartService.offerAliasId,
        this.shoppingCartService.propId,
        UiNewOfferPhotoTermsComponent,
      );
    }
  }

  checkIfOfferInCart(): boolean {
    return this.shoppingCartService.checkIfOfferInCart(
      this.offerAliasId,
      this.offer?.offer_set_id || '',
      this.offer?.offer_id || ''
    );
  }

  toggleTextContainerExpanded(index: number): { [key: string]: boolean } {
    const isExpanded = this.isExpanded(index);
    return {
      'text-container': !isExpanded,
      'text-container-expanded': isExpanded
    };
  }

  isCollapsed(index: number): boolean {
    // Any non-selected card should render as collapsed, even if this component
    // instance previously expanded its local `collapsed` state.
    return index !== this.sharedService.currentIndex ? true : this.collapsed;
  }

  isExpanded(index: number): boolean {
    return !this.isCollapsed(index) && index === this.sharedService.currentIndex;
  }

  toggleCollapse(index: number): void {
    if (index !== this.sharedService.currentIndex) {
      this.collapsed = false;
    } else {
      this.collapsed = !this.collapsed;
    }

    this.setMaxListIndex(this.collapsed, index);

    if (this.offers) {
      this.sharedService.setSelectedOffer(index, this.offers, true);
    }

    // Ensure only the currently-expanded card is marked as content-expanded.
    // Without this, previously-expanded cards can keep expanded styling until clicked again.
    try {
      const doc = this.hostRef.nativeElement.ownerDocument;
      doc
        ?.querySelectorAll('.offer-card.content-expanded')
        ?.forEach(el => this.renderer.removeClass(el, 'content-expanded'));
    } catch (e) {
      // ignore
    }

    // Reflect content expanded/collapsed state on the parent .offer-card so other
    // components (like the icon component) can respond to content visibility.
    const offerCard = this.hostRef.nativeElement.closest('.offer-card') as HTMLElement | null;
    if (offerCard) {
      if (this.isExpanded(index)) {
        this.renderer.addClass(offerCard, 'content-expanded');
      } else {
        this.renderer.removeClass(offerCard, 'content-expanded');
      }
    }
    // Notify interested components (icons) that content expansion changed so they
    // can relayout synchronously.
    try {
      const evt = new CustomEvent('ui:new:contentToggled', { detail: { index }, bubbles: true });
      window.dispatchEvent(evt);
    } catch (e) {
      // ignore
    }
  }

  tapForDetails(i: number, offer: Offer): string[] {
    const maxItems = this.sharedService.currentIndex !== i ? this.MIN_LIST_INDEX : this.max_list_index;
    let amenities = offer.amenities.upsell_attributes.slice(0, maxItems);

    if (offer.amenities.summary) {
      amenities.unshift('Summary: ' + offer.amenities.summary);
    }

    return amenities;
  }

  contentStyle(i: number, j: number): { [key: string]: boolean } {
    const isCollapsed = this.sharedService.currentIndex !== i ||
                        (this.sharedService.currentIndex === i && this.collapsed);

    return {
      'even-more-opacity': j === 0 && isCollapsed,
      'more-opacity': j === 1 && isCollapsed,
      'some-opacity': j >= 2 && isCollapsed,
      'card-content-expanded': this.sharedService.currentIndex === i && !this.collapsed
    };
  }

  setMaxListIndex(collapsed: boolean, index: number): void {
    if (!this.offers) return;

    this.max_list_index = collapsed
      ? this.MIN_LIST_INDEX
      : this.offers[index].amenities.upsell_attributes.length;
  }

  offerIndex(index: number, viewMoreToggle: boolean, numOffers: number): string {
    return Utils.offerIndex(index, viewMoreToggle, numOffers);
  }

  checkIfDisabled(): boolean {
    const offers = this.shoppingCartService.getOffersByAliasId(this.offerAliasId);
    if (!offers || !this.offer) {
      return false;
    }

    const exactMatch = offers.find(entry =>
      entry.offerSetId === this.offer!.offer_set_id &&
      entry.offerId === this.offer!.offer_id
    );

    const offerSetMatch = offers.find(entry =>
      entry.offerSetId === this.offer!.offer_set_id
    );

    return !exactMatch && !!offerSetMatch;
  }

  shouldShowStrike(offer: Offer | null): boolean {
    if (!offer || offer.bar_price == null || offer.ofr_price == null) {
      return false;
    }

    const bar = Number(offer.bar_price);
    const ofr = Number(offer.ofr_price);

    if (!isFinite(bar) || bar <= 0 || !isFinite(ofr)) {
      return false;
    }

    const discountPercent = ((bar - ofr) / bar) * 100;
    return discountPercent >= 5;
  }
}
